import {
  FilterSelector,
  KeyValue,
} from '@delmonicos/shared-design-system-react/src/components/filter-selector/filter-selector';
import {
  useDebounce,
  usePaginatedFilter,
  usePaginatedFilterControls,
} from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { CustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/outputs/customers/customer.model';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetCustomersPaginatedQuery } from '@service/cpms-bo-customer.service';

const LIMIT = 4;

interface CustomerFilterControllerProps {
  onChange: (items: string[]) => void;
  selectedItemIds: string[];
}

export const CustomerFilterController = ({
  onChange,
  selectedItemIds,
}: CustomerFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [search, setSearch, offset, incrementOffset, resetOffset] =
    usePaginatedFilterControls(LIMIT);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isFetching } = useCpmsBoGetCustomersPaginatedQuery({
    limit: LIMIT,
    offset,
    name: debouncedSearch ? `~${debouncedSearch}` : '~',
  });

  const handleMap = useCallback(
    (customer: CustomerModel): KeyValue => ({
      key: customer.id,
      value: customer.name,
    }),
    [],
  );

  const { items, updateItems } = usePaginatedFilter<KeyValue>({
    data,
    offset,
    onMap: handleMap,
  });

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setIsTyping(false);
    }
  }, [debouncedSearch]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <FilterSelector
      items={items}
      isLoading={isFetching || isTyping}
      initialSelectedItemIds={selectedItemIds}
      onChange={onChange}
      onSearchChange={(val) => {
        setSearch(val);
        resetOffset();
        updateItems([]);
      }}
      onOffsetChange={incrementOffset}
      labels={{
        title: t('delmonicos-shared-sdk.outputs-invoice.customer'),
        search: t('common.filter.search'),
        seeMore: t('common.filter.seeMore'),
      }}
      icon="dataflow-04"
      hasSeeMore={!!data?.hasNextPage}
    />
  );
};
